import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  isBefore,
  isSameDay,
  startOfDay,
  endOfDay,
  isAfter,
  addMonths,
  parseISO,
} from "date-fns";
import { pl } from "date-fns/locale";
import { createPortal } from "react-dom";
import deleteButtonIcon from "../../media/delete-button.svg";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import httpClient from "../../services/httpClient";
import { useNotification } from "../../components/NotificationContext";
import { useAuth } from "../../AuthContext";
import eventBus from "../../components/eventBus";
import Logger from "../../utils/Logger";
import JSZip from "jszip";
import "../../styleSheet/main/reservations/roomsReservations/roomsReservations.scss";

const EditRoomReservation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  // const { showNotification } = useNotification();
  const { userData } = useAuth();

  const [date, setDate] = useState(null);
  const [startHour, setStartHour] = useState(null);
  const [endHour, setEndHour] = useState(null);
  const [tripPurpose, setTripPurpose] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [errors, setErrors] = useState({});
  const [hoveredIcon, setHoveredIcon] = useState(false);
  const [isDatesSet, setIsDatesSet] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [newParticipantId, setNewParticipantId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [confirmationData, setConfirmationData] = useState({});
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReservation, setIsLoadingReservation] = useState(true);
  const [originalReservation, setOriginalReservation] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);

  const getEmail = () => userData.userEmail;
  const getName = () => userData.userName;

  // Pobierz listę pracowników najpierw
  useEffect(() => {
    const fetchEmployees = async () => {
      // Wait until we have date, time and original reservation data
      if (!date || !startHour || !endHour || !id) {
        return;
      }

      try {
        // Format datetime as ISO string with timezone
        const startDateTime = new Date(
          `${format(date, "yyyy-MM-dd")}T${format(startHour, "HH:mm:ss")}`,
        ).toISOString();
        const endDateTime = new Date(
          `${format(date, "yyyy-MM-dd")}T${format(endHour, "HH:mm:ss")}`,
        ).toISOString();

        const response = await httpClient.get(
          `/Users/available-employees/except?reservationId=${id}&StartTime=${encodeURIComponent(startDateTime)}&EndTime=${encodeURIComponent(endDateTime)}`,
          {
            withAuth: true,
          },
        );
        setEmployees(response.data);
      } catch (error) {
        Logger.error("Błąd przy pobieraniu uczestników", error);
      }
    };

    fetchEmployees();
  }, [date, startHour, endHour, id]);

  // Pobierz dane istniejącej rezerwacji
  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setIsLoadingReservation(true);
        const response = await httpClient.get(`/RoomReservations/${id}`, {
          withAuth: true,
        });

        const reservation = response.data;
        setOriginalReservation(reservation);

        // Ustaw dane w formularzu
        // Parse start and end times (format: yyyy-MM-ddTHH:mm:ss)
        const startDateTime = parseISO(reservation.startTime);
        const endDateTime = parseISO(reservation.endTime);

        // Set date from startTime
        setDate(startDateTime);
        setStartHour(startDateTime);
        setEndHour(endDateTime);
        setIsDatesSet(true); // Dodane - umożliwia wyświetlenie formularza

        setTripPurpose(reservation.title || "");
        setDescription(reservation.description || "");
        setSelectedRoom(reservation.room);

        // Ustaw załącznik jeśli istnieje
        if (reservation.preSignedFileUrl || reservation.hasAttachment) {
          setExistingAttachment({
            url: reservation.preSignedFileUrl,
            fileName: reservation.attachmentFileName || "Załącznik",
          });
        }

        // Ustaw uczestników - new API uses 'invitations' instead of 'attendees'
        const attendeesData = reservation.invitations || reservation.attendees;
        if (attendeesData) {
          if (
            typeof attendeesData === "object" &&
            !Array.isArray(attendeesData)
          ) {
            Logger.log("Object keys:", Object.keys(attendeesData));
            Logger.log("Object values:", Object.values(attendeesData));
          }
          let attendeesList = [];

          if (Array.isArray(attendeesData)) {
            // Jeśli attendees to tablica
            attendeesList = attendeesData.map((attendee) => {
              // Jeśli attendee to string w formacie "name - email"
              if (typeof attendee === "string" && attendee.includes(" - ")) {
                const [name, email] = attendee.split(" - ");
                return { name: name.trim(), email: email.trim() };
              }
              // Jeśli attendee to obiekt
              let name = "";
              if (attendee.employee) {
                name = attendee.employee;
              } else if (attendee.userFullName) {
                // New API format
                name = attendee.userFullName;
              } else if (attendee.name) {
                name = attendee.name;
              } else if (attendee.firstName && attendee.lastName) {
                name = `${attendee.firstName} ${attendee.lastName}`;
              } else if (attendee.firstName) {
                name = attendee.firstName;
              } else if (attendee.lastName) {
                name = attendee.lastName;
              }

              return {
                name: name,
                email:
                  attendee.userEmail ||
                  attendee.emailAddress ||
                  attendee.email ||
                  "",
              };
            });
          } else if (typeof attendeesData === "object") {
            // Jeśli attendees to obiekt (klucz-wartość)
            attendeesList = Object.keys(attendeesData).map((nameKey) => {
              // Sprawdź czy klucz zawiera już format "name - email"
              if (nameKey.includes(" - ")) {
                const [name, email] = nameKey.split(" - ");
                Logger.log(
                  "Found format with dash - Name:",
                  name,
                  "Email:",
                  email,
                );
                return { name: name.trim(), email: email.trim() };
              } else {
                // Stary format gdzie email może być w wartości
                const attendeeData = attendeesData[nameKey];
                const email =
                  attendeeData?.email || attendeeData?.emailAddress || "";
                Logger.log("Standard format - Name:", nameKey, "Email:", email);
                return {
                  name: nameKey,
                  email: email,
                };
              }
            });
          } else if (typeof attendeesData === "string") {
            // Jeśli attendees to string z listą uczestników
            const attendeesArray = attendeesData.split(",");
            attendeesList = attendeesArray.map((attendee) => {
              const trimmed = attendee.trim();
              if (trimmed.includes(" - ")) {
                const [name, email] = trimmed.split(" - ");
                return { name: name.trim(), email: email.trim() };
              }
              return { name: trimmed, email: "" };
            });
          }

          // Próba dopasowania emaili z listy pracowników jeśli brakuje
          if (employees.length > 0) {
            attendeesList = attendeesList.map((attendee) => {
              if (!attendee.email) {
                const matchingEmployee = employees.find(
                  (emp) => `${emp.firstName} ${emp.lastName}` === attendee.name,
                );
                if (matchingEmployee) {
                  Logger.log(
                    "Found matching employee for",
                    attendee.name,
                    "email:",
                    matchingEmployee.email,
                  );
                  return { ...attendee, email: matchingEmployee.email };
                }
              }
              return attendee;
            });
          }

          Logger.log("Final attendees list:", attendeesList);
          setParticipants(attendeesList);
        }

        // Sprawdź czy użytkownik ma uprawnienia do edycji tej rezerwacji
        // Tylko twórca rezerwacji lub administrator może edytować
        const isCreator = reservation.createdBy === userData.userName;
        const isAdmin = userData.userRole === "admin";

        if (!isCreator && !isAdmin) {
          Logger.warn(
            t("reservations.common.permissionDenied"),
            userData.userName,
            "Creator:",
            reservation.createdBy,
          );
          alert(t("reservations.common.permissionDeniedLong"));
          navigate("/rezerwacje/sale-konferencyjne/");
          return;
        }
      } catch (error) {
        Logger.error(t("reservations.common.fetchDataError"), error);
        // showNotification("Nie udało się załadować danych rezerwacji.", "error");
        navigate("/rezerwacje/sale-konferencyjne/");
      } finally {
        setIsLoadingReservation(false);
      }
    };

    if (id) {
      fetchReservation();
    }
  }, [id, navigate, userData]);

  // Pobierz dostępne sale gdy zmienią się daty
  useEffect(() => {
    const fetchRooms = async () => {
      if (!date || !startHour || !endHour) {
        return;
      }

      try {
        // Format datetime as yyyy-MM-ddTHH:mm:ss
        const startDateTime = `${format(date, "yyyy-MM-dd")}T${format(
          startHour,
          "HH:mm:ss",
        )}`;
        const endDateTime = `${format(date, "yyyy-MM-dd")}T${format(
          endHour,
          "HH:mm:ss",
        )}`;

        const response = await httpClient.get(
          `/Rooms/available-rooms/except?reservationid=${id}&startTime=${encodeURIComponent(
            startDateTime,
          )}&endTime=${encodeURIComponent(endDateTime)}`,
          { withAuth: true },
        );

        const data = response.data;

        if (Array.isArray(data)) {
          // Show all rooms (available and unavailable)
          let roomsList = data;

          // Always include the current room even if it's not in the available list
          if (
            originalReservation &&
            !roomsList.find((room) => room.name === originalReservation.room)
          ) {
            // Check if current room exists in the full data list
            const currentRoomInList = data.find(
              (r) => r.name === originalReservation.room,
            );
            if (currentRoomInList) {
              // Add it even if not available (user is editing their existing reservation)
              roomsList.push(currentRoomInList);
            } else {
              // Room not in list at all, create entry (shouldn't happen normally)
              roomsList.push({
                id: originalReservation.roomId || null,
                name: originalReservation.room,
                isAvailable: true, // Assume available since it's the current reservation
              });
            }
          }

          setRooms(roomsList);
          setIsDatesSet(true);
        }
      } catch (error) {
        Logger.error("Błąd podczas pobierania sal:", error);
      }
    };

    fetchRooms();
  }, [date, startHour, endHour, originalReservation, id]);

  const handleRoomSelection = (room) => {
    setSelectedRoom(selectedRoom === room ? null : room);
  };

  const handleDateChange = (date) => {
    setDate(date);
  };

  const handleStartHourChange = (date) => {
    setStartHour(date);
    if (date && endHour) {
      if (isSameDay(date, endHour)) {
        if (!isBefore(date, endHour)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            startHour:
              "Godzina rozpoczęcia musi być wcześniejsza niż godzina zakończenia",
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, startHour: null }));
        }
      }
    }
  };

  const handleEndHourChange = (dateNow) => {
    setEndHour(dateNow);
  };

  const handleAddParticipant = () => {
    if (newParticipantId) {
      const selectedEmployee = employees.find(
        (emp) => emp.id === newParticipantId,
      );
      if (!selectedEmployee) {
        return;
      }
      if (selectedEmployee.email === getEmail()) {
        return;
      }
      if (selectedEmployee.busy === true) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          participants: t("reservations.rooms.employeeBusy"),
        }));
        return;
      }
      if (selectedEmployee) {
        setParticipants([
          ...participants,
          {
            name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
            email: selectedEmployee.email,
            id: selectedEmployee.id,
            busy: selectedEmployee.busy,
          },
        ]);
        setErrors((prevErrors) => ({ ...prevErrors, participants: null }));
      }
      setNewParticipantId("");
    }
  };

  const handleParticipantChange = (participantId) => {
    const selectedEmployee = employees.find((emp) => emp.id === participantId);

    if (selectedEmployee?.busy === true) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        participants: t("reservations.rooms.employeeBusy"),
      }));
      setNewParticipantId("");
      return;
    }

    setErrors((prevErrors) => ({ ...prevErrors, participants: null }));
    setNewParticipantId(participantId);
  };

  const participantOptions = employees
    .filter((employee) => employee.email !== getEmail())
    .filter(
      (employee) =>
        !participants.some((participant) => participant.id === employee.id),
    )
    .sort((a, b) => {
      if (a.busy !== b.busy) {
        return a.busy ? 1 : -1;
      }
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((employee) => ({
      value: employee.id,
      label: `${employee.firstName} ${employee.lastName}${
        employee.busy ? ` (${t("reservations.rooms.busy")})` : ""
      }`,
      isBusy: employee.busy === true,
    }));

  const selectedParticipantOption =
    participantOptions.find((option) => option.value === newParticipantId) ||
    null;

  const participantSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#000000",
      borderColor: state.isFocused ? "#ff9900" : "#efefef",
      borderRadius: 0,
      minHeight: "46px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#ff9900",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#000000",
      border: "1px solid #efefef",
      borderRadius: 0,
      zIndex: 20,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    input: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#111111" : "#000000",
      color: "#ffffff",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.65 : 1,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#efefef",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#ffffff",
      "&:hover": {
        color: "#ffffff",
      },
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#000000",
    }),
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const handleUpdateReservation = async () => {
    console.log("handleUpdateReservation called");
    console.log("Current state:", {
      date,
      startHour,
      endHour,
      tripPurpose,
      selectedRoom,
      participants,
    });

    const newErrors = {};

    if (!date) {
      newErrors.date = t("reservations.rooms.errors.selectDate");
    }
    if (!startHour) {
      newErrors.startHour = t("reservations.rooms.errors.selectStartTime");
    }
    if (!endHour) {
      newErrors.endHour = t("reservations.rooms.errors.selectEndTime");
    }

    if (startHour && endHour && isSameDay(startHour, endHour)) {
      if (!isBefore(startHour, endHour)) {
        newErrors.endHour = t("reservations.rooms.errors.invalidTimeRange");
      }
    }

    if (!tripPurpose) {
      newErrors.tripPurpose = t("reservations.rooms.errors.enterPurpose");
    }
    if (!selectedRoom) {
      newErrors.selectedRoom = t("reservations.rooms.errors.selectRoom");
    }

    console.log("Validation errors:", newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.log("Setting errors, not showing confirmation");
      setErrors(newErrors);
    } else {
      console.log("No errors, showing confirmation modal");
      setConfirmationData({
        room: selectedRoom,
        date: format(date, "dd-MM-yyyy"),
        startHour: format(startHour, "HH:mm"),
        endHour: format(endHour, "HH:mm"),
        participants: participants.map((p) => p.name),
        tripPurpose: tripPurpose,
        description: description,
        files: files,
        existingAttachment: existingAttachment,
        removeAttachment: removeAttachment,
      });

      setShowConfirmation(true);
      console.log("Confirmation modal should be visible now");
    }
  };

  const handleConfirm = async () => {
    console.log("handleConfirm called - starting update");
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Format datetime as local datetime string (yyyy-MM-ddTHH:mm:ss)
      const startTime =
        date && startHour
          ? `${format(date, "yyyy-MM-dd")}T${format(startHour, "HH:mm:ss")}`
          : "";
      const endTime =
        date && endHour
          ? `${format(date, "yyyy-MM-dd")}T${format(endHour, "HH:mm:ss")}`
          : "";

      // Add form fields
      formData.append(
        "roomId",
        rooms.find((room) => room.name === selectedRoom)?.id,
      );
      formData.append("startTime", startTime);
      formData.append("endTime", endTime);
      formData.append("title", tripPurpose);
      formData.append("description", description || "");
      formData.append("removeAttachment", removeAttachment);

      // Add participant IDs
      Logger.log("Current participants:", participants);
      Logger.log("Available employees:", employees.length);

      const participantIds = participants
        .map((participant) => {
          Logger.log("Looking for participant:", participant);

          // First try to find by id if available
          let employee;
          if (participant.id) {
            employee = employees.find((emp) => emp.id === participant.id);
          }

          // If not found by id, try by email
          if (!employee && participant.email) {
            employee = employees.find((emp) => emp.email === participant.email);
          }

          // If still not found, try by name match
          if (!employee && participant.name) {
            employee = employees.find(
              (emp) => `${emp.firstName} ${emp.lastName}` === participant.name,
            );
          }

          Logger.log("Found employee:", employee);
          return employee?.id || participant.id; // Use participant.id as fallback
        })
        .filter((id) => id !== undefined);

      Logger.log("Participant IDs to send:", participantIds);

      participantIds.forEach((id) => {
        formData.append("participantIds", id);
      });

      // Handle file attachment (zip if multiple files)
      if (files.length > 1) {
        const zip = new JSZip();
        files.forEach((file) => zip.file(file.name, file));
        const zipFileName = `sala-konferencyjna-${Date.now()}.zip`;
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipFile = new File([zipBlob], zipFileName, {
          type: "application/zip",
        });
        formData.append("attachment", zipFile);
      } else if (files.length === 1) {
        formData.append("attachment", files[0]);
      }

      Logger.log("Dane aktualizacji rezerwacji FormData:", {
        roomId: rooms.find((room) => room.name === selectedRoom)?.id,
        startTime,
        endTime,
        title: tripPurpose,
        description,
        participantIds,
        removeAttachment,
        filesCount: files.length,
      });

      await httpClient.put(
        `/RoomReservations/${id}/edit-reservation`,
        formData,
        {
          withAuth: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // showNotification(
      //   "Rezerwacja została zaktualizowana pomyślnie!",
      //   "success"
      // );
      setShowConfirmation(false);
      eventBus.emit("room-reservation-updated", {});
      navigate("/rezerwacje/sale-konferencyjne/");
    } catch (error) {
      Logger.error(t("reservations.common.updateErrorPrefix"), error);
      Logger.error("Error response:", error.response?.data);
      Logger.error("Error status:", error.response?.status);

      // Sprawdź czy to błąd autoryzacji
      const isAuthError =
        error.response?.status === 403 ||
        error.response?.status === 401 ||
        (error.response?.status === 400 &&
          error.response?.data?.some?.(
            (err) =>
              err.message?.toLowerCase().includes("not authorized") ||
              err.message?.toLowerCase().includes("authorization"),
          ));

      if (isAuthError) {
        alert(t("reservations.common.permissionDeniedWithAdmin"));
        navigate("/rezerwacje/sale-konferencyjne/");
        return;
      }

      // Lepsze logowanie błędów walidacji
      if (error.response?.data && Array.isArray(error.response.data)) {
        Logger.error("Validation errors:");
        error.response.data.forEach((err, index) => {
          Logger.error(`  ${index + 1}. ${JSON.stringify(err)}`);
        });
      }

      // Wyświetl szczegółowy komunikat błędu
      let errorMessage = "Nieznany błąd";
      if (error.response?.data) {
        if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data
            .map(
              (err) => err.message || err.errorMessage || JSON.stringify(err),
            )
            .join("\n");
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`${t("reservations.common.updateErrorPrefix")}\n${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleCancelChange = () => {
    navigate("/rezerwacje/sale-konferencyjne/");
  };

  const handleIconClick = () => {
    navigate("/rezerwacje/sale-konferencyjne");
  };

  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancel}
          />
          <div className="areYouSure">
            <p>{t("reservations.rooms.confirmUpdate")}</p>
          </div>
          <div>
            <p>
              <strong>{t("reservations.rooms.room")}: </strong>
              {confirmationData.room}
            </p>
            <p>
              <strong>{t("reservations.rooms.date")}: </strong>{" "}
              {confirmationData.date}
            </p>
            <p>
              <strong>{t("reservations.rooms.startTime")}: </strong>
              {confirmationData.startHour}
            </p>
            <p>
              <strong>{t("reservations.rooms.endTime")}: </strong>
              {confirmationData.endHour}
            </p>
            <p>
              <strong>{t("reservations.rooms.participants")}: </strong>
              {confirmationData.participants.join(", ")}
            </p>
            <p>
              <strong>{t("reservations.rooms.meetingPurpose")}: </strong>
              {confirmationData.tripPurpose}
            </p>
            {confirmationData.description && (
              <p>
                <strong>{t("reservations.rooms.description")}: </strong>
                {confirmationData.description}
              </p>
            )}
            {confirmationData.files && confirmationData.files.length > 0 && (
              <div>
                <strong>{t("reservations.rooms.newAttachments")}</strong>
                <ul style={{ marginLeft: "20px" }}>
                  {confirmationData.files.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="confirmation-buttons">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t("reservations.rooms.updatingReservation")}</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirm}>{t("common.yes")}</button>
                <button onClick={handleCancelChange}>{t("common.no")}</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const decideMinTimeForStartHour = () => {
    if (!date) return startOfDay(new Date());
    if (isSameDay(date, new Date())) {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);
      return now;
    }
    return startOfDay(date);
  };

  if (isLoadingReservation) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t("reservations.rooms.loadingReservation")}</p>
      </div>
    );
  }

  return (
    <div className="roomsa-reservations-container">
      <div className="main-site" style={{ justifyContent: "space-between" }}>
        <h1>{t("reservations.rooms.editReservation")}</h1>
        <div
          className="conference-rooms-orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHoveredIcon(true)}
          onMouseLeave={() => setHoveredIcon(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hoveredIcon ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <div className="conference-rooms-main-container">
        <div style={{ width: "100%", padding: "0px", marginTop: "10px" }}>
          <div
            className="conference-rooms-date-picker-container"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, max-content)",
              justifyContent: "space-between",
              width: "100%",
              borderBottom: "1px solid orange",
              paddingBottom: "3rem",
              marginBottom: "3rem",
            }}
          >
            <div style={{ width: "250px" }} className="input-group">
              <label htmlFor="date">
                {t("reservations.rooms.meetingDate")}
              </label>
              <DatePicker
                selected={date}
                onChange={handleDateChange}
                dateFormat="dd-MM-yyyy"
                locale={pl}
                placeholderText={t("reservations.rooms.selectDate")}
                minDate={new Date()}
                maxDate={addMonths(new Date(), 6)}
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
            <div style={{ width: "250px" }} className="input-group">
              <label htmlFor="startHour">
                {t("reservations.rooms.startTime")}
              </label>
              <DatePicker
                selected={startHour}
                onChange={handleStartHourChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption={t("reservations.common.time")}
                dateFormat="HH:mm"
                locale={pl}
                placeholderText={t("reservations.rooms.selectTime")}
                minDate={date ? date : new Date()}
                minTime={decideMinTimeForStartHour()}
                maxTime={endHour ? endHour : endOfDay(new Date())}
                filterTime={(time) => isBefore(time, endHour) || !endHour}
                disabled={!date}
              />
              {errors.startHour && (
                <span className="error-message">{errors.startHour}</span>
              )}
            </div>
            <div style={{ width: "250px" }} className="input-group">
              <label htmlFor="endHour">
                {t("reservations.rooms.endTime")}
              </label>
              <DatePicker
                selected={endHour}
                onChange={handleEndHourChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption={t("reservations.common.time")}
                dateFormat="HH:mm"
                locale={pl}
                placeholderText={t("reservations.rooms.selectTime")}
                minDate={date ? date : new Date()}
                minTime={startHour ? startHour : startOfDay(new Date())}
                maxTime={endOfDay(new Date())}
                filterTime={(time) => isAfter(time, startHour) || !startHour}
                disabled={!date}
              />
              {errors.endHour && (
                <span className="error-message">{errors.endHour}</span>
              )}
            </div>
          </div>

          <div className="conference-rooms-reservation-details-rooms-picker">
            {isDatesSet && (
              <div className="available-group">
                <label>{t("reservations.rooms.conferenceRooms")}</label>
                <div
                  className="cars-list"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, auto)",
                    width: "100%",
                    justifyContent: "space-between",
                    marginTop: "2rem",
                  }}
                >
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      className={`car-item ${
                        !room.isAvailable ? "disabled" : ""
                      }`}
                      onClick={() =>
                        room.isAvailable && handleRoomSelection(room.name)
                      }
                      style={{
                        opacity: room.isAvailable ? 1 : 0.5,
                        cursor: room.isAvailable ? "pointer" : "not-allowed",
                        width: "200px",
                        gap: "15px",
                        display: "flex",
                        alignItems: "center",
                        fontWeight: "bold",
                      }}
                    >
                      <span
                        className={`car-checkbox ${
                          selectedRoom === room.name && room.isAvailable
                            ? "checked"
                            : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (room.isAvailable) {
                            handleRoomSelection(room.name);
                          }
                        }}
                      />
                      {room.name}
                      {originalReservation &&
                        room.name === originalReservation.room && (
                          <span
                            style={{
                              color: "#ff9900",
                              fontSize: "0.9em",
                              marginLeft: "10px",
                            }}
                          >
                            ({t("reservations.rooms.currentRoom")})
                          </span>
                        )}
                    </div>
                  ))}
                </div>
                {errors.selectedRoom && (
                  <span className="error-message">{errors.selectedRoom}</span>
                )}
              </div>
            )}
          </div>

          <div
            className="conference-rooms-reservation-details-middle-container"
            style={{
              width: "100%",
              marginTop: "3rem",
              display: isDatesSet ? "flex" : "none",
              flexDirection: "column",
              gap: "2rem",
            }}
          >
            {isDatesSet && (
              <div className="two-column-layout">
                <div className="left-column">
                  <div className="input-group">
                    <label htmlFor="files">
                      {t("reservations.rooms.attachments")}
                    </label>
                    <label htmlFor="files" className="custom-file-upload">
                      {t("reservations.rooms.addFiles")}
                    </label>
                    <input
                      type="file"
                      id="files"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    {existingAttachment && !removeAttachment && (
                      <div
                        style={{
                          marginTop: "15px",
                          padding: "15px",
                          backgroundColor: "rgba(255, 153, 0, 0.1)",
                          border: "1px solid #ff9900",
                          marginBottom: "15px",
                        }}
                      >
                        <h4
                          style={{
                            color: "#efefef",
                            marginBottom: "10px",
                            fontSize: "1em",
                          }}
                        >
                          {t("reservations.rooms.currentAttachment")}
                        </h4>
                        <a
                          href={existingAttachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#ff9900",
                            textDecoration: "underline",
                          }}
                        >
                          {existingAttachment.fileName}
                        </a>
                        <div style={{ marginTop: "10px" }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "#efefef",
                              cursor: "pointer",
                              fontSize: "0.95em",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={removeAttachment}
                              onChange={(e) =>
                                setRemoveAttachment(e.target.checked)
                              }
                              style={{
                                width: "auto",
                                marginRight: "8px",
                                cursor: "pointer",
                                accentColor: "#ff9900",
                              }}
                            />
                            {t("reservations.rooms.removeCurrentAttachment")}
                          </label>
                        </div>
                      </div>
                    )}
                    {files.length > 0 && (
                      <div className="selected-files">
                        <h4>{t("reservations.rooms.newAttachments")}</h4>
                        <ul>
                          {files.map((file, index) => (
                            <li key={index} className="file-item">
                              <span>{file.name}</span>
                              <img
                                src={deleteButtonIcon}
                                alt="Usuń"
                                onClick={() => handleRemoveFile(index)}
                                className="delete-button-icon"
                                style={{
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  marginLeft: "10px",
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="input-group">
                    <label htmlFor="participants">
                      {t("reservations.rooms.participants")}
                    </label>
                    <Select
                      inputId="newParticipant"
                      options={participantOptions}
                      value={selectedParticipantOption}
                      onChange={(option) =>
                        handleParticipantChange(option?.value ?? "")
                      }
                      placeholder={t("reservations.rooms.selectParticipant")}
                      styles={participantSelectStyles}
                      isOptionDisabled={(option) => option.isBusy}
                      isClearable
                    />
                    <button onClick={handleAddParticipant}>
                      {t("reservations.rooms.addParticipant")}
                    </button>
                    {errors.participants && (
                      <span className="error-down-message">
                        {errors.participants}
                      </span>
                    )}
                    <ul className="participants-list">
                      {participants.map((participant, index) => (
                        <li key={index}>
                          <span>{participant.name}</span>
                          <img
                            src={deleteButtonIcon}
                            alt="Usuń"
                            onClick={() => handleRemoveParticipant(index)}
                            className="delete-button-icon"
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="right-column">
                  <div className="input-group">
                    <label htmlFor="tripPurpose">
                      {t("reservations.rooms.meetingPurpose")}
                    </label>
                    <input
                      type="text"
                      id="tripPurpose"
                      value={tripPurpose}
                      onChange={(e) => setTripPurpose(e.target.value)}
                      placeholder={t("reservations.rooms.enterPurpose")}
                    />
                    {errors.tripPurpose && (
                      <span className="error-message">
                        {errors.tripPurpose}
                      </span>
                    )}
                  </div>

                  <div className="input-group">
                    <label htmlFor="description">
                      {t("reservations.rooms.description")}
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("reservations.rooms.enterDescription")}
                    />
                  </div>
                </div>
              </div>
            )}
            {isDatesSet && (
              <button
                className="reservation-button"
                onClick={handleUpdateReservation}
                style={{
                  width: "50%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {t("reservations.rooms.updateReservation")}
              </button>
            )}
          </div>
        </div>
      </div>

      {renderConfirmationModal()}
    </div>
  );
};

export default EditRoomReservation;
